import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FilterByForm, FilterField, FilterOption } from 'models/Filters';
import { Button } from 'ncoded-component-library';
import { FieldArray } from 'react-final-form-arrays';
import { Field, useFormState } from 'react-final-form';
import SelectField from 'components/SelectField';
import TrashIcon from 'icons/Trash.icon';
import ResolveMultiSelectField from 'components/ResolveMultiSelectField';
import { ResolveMultiSelectRef } from 'components/ResolveMultiSelect/types';
import PlusIcon from 'icons/Plus.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import { getArrayLength, isSingleElementArray, sortOptions } from 'utils';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import { SMOOTH_SCROLL_CONFIGURATION, DEBOUNCE_TIME } from 'data/global';
import { FocusFieldRef } from 'models/FieldFocusableModal';
import ResultsCount from '../ResultsCount';
import { GetResultsCount } from '../ResultsCount/ResultsCount.component';

import './MultiFilterOptions.styles.scss';

export const INITIAL_VALUE = {
  name: '',
  value: '',
} as FilterField;

export type MultiFilterOptionsProps = {
  className?: string;
  filters: FilterOption[];
  // Set to false to keep the declared filter order, e.g. to mirror the table's
  // column order instead of listing them alphabetically.
  shouldSortFilters?: boolean;
  getResultsCount?: GetResultsCount;
};

const MultiFilterOptions: React.ForwardRefRenderFunction<
  FocusFieldRef,
  MultiFilterOptionsProps
> = (props, ref) => {
  const {
    className,
    filters,
    shouldSortFilters = true,
    getResultsCount,
  } = props;

  const classes = classNames(
    'multi-filter-options',
    'multi-fields-options',
    className,
  );

  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const selectRefs = useRef(new Map<string, ResolveMultiSelectRef>());
  const valueRefs = useRef(new Map<string, HTMLDivElement>());

  useImperativeHandle(
    ref,
    () => ({
      focusField: (fieldKey: string) => {
        rowRefs.current
          .get(fieldKey)
          ?.scrollIntoView({ ...SMOOTH_SCROLL_CONFIGURATION, block: 'center' });

        setTimeout(() => {
          const select = selectRefs.current.get(fieldKey);

          if (select) {
            select.open();
            return;
          }

          valueRefs.current
            .get(fieldKey)
            ?.querySelector<HTMLElement>('.ncoded-select__value')
            ?.click();
        }, DEBOUNCE_TIME);
      },
    }),
    [],
  );

  const {
    values: { filters: formFilters },
  } = useFormState<FilterByForm>();

  const sortedFilters = useMemo(
    () => (shouldSortFilters ? sortOptions(filters) : filters),
    [filters, shouldSortFilters],
  );

  const getRowRef = useCallback(
    (filterName: string) => (el: HTMLDivElement | null) => {
      if (!filterName) return;
      if (el) rowRefs.current.set(filterName, el);
      else rowRefs.current.delete(filterName);
    },
    [],
  );

  const getSelectRef = useCallback(
    (filterName: string) => (el: ResolveMultiSelectRef | null) => {
      if (!filterName) return;
      if (el) selectRefs.current.set(filterName, el);
      else selectRefs.current.delete(filterName);
    },
    [],
  );

  const getValueRef = useCallback(
    (filterName: string) => (el: HTMLDivElement | null) => {
      if (!filterName) return;
      if (el) valueRefs.current.set(filterName, el);
      else valueRefs.current.delete(filterName);
    },
    [],
  );

  return (
    <div className={classes}>
      <FieldArray name="filters">
        {({ fields }) => (
          <>
            <div className="multi-filter-options__rows">
              {fields.map((name, index) => {
                const isFirstElement = index === 0;

                const { name: filterName, value: filterValue } =
                  fields.value[index] || {};

                const selectedFilter = filters.find(
                  ({ value }) => value === filterName,
                );

                const {
                  isSearchable,
                  shouldSortOptions = true,
                  options = [],
                  render,
                } = selectedFilter || {};

                const selectedFilterOptions = shouldSortOptions
                  ? sortOptions(options)
                  : options;

                return (
                  <div key={name} className="row" ref={getRowRef(filterName)}>
                    <div>
                      <p>{t(isFirstElement ? 'filterBy' : 'thenBy')}</p>

                      <Field
                        name={`${name}.name`}
                        component={SelectField}
                        renderAsPortal={!isPhablet}
                        portalClassName="multi-filter-select"
                        icon={<ChevrondownIcon />}
                        customOnChange={(name: string) =>
                          fields.update(index, { name, value: [] })
                        }
                        options={sortedFilters.map(({ value, label }) => ({
                          value,
                          label,
                          disabled: formFilters.find(
                            ({ name }) => name === value,
                          ),
                        }))}
                      />
                    </div>

                    <div ref={getValueRef(filterName)}>
                      {filterName &&
                        (render ? (
                          render({
                            value: filterValue,
                            onChange: (value) =>
                              fields.update(index, { name: filterName, value }),
                            selectRef: getSelectRef(filterName),
                          })
                        ) : selectedFilter?.isMultiple ? (
                          <Field name={`${name}.value`}>
                            {(fieldProps) => (
                              <ResolveMultiSelectField
                                ref={getSelectRef(filterName)}
                                {...fieldProps}
                                renderAsPortal={!isPhablet}
                                hasCheckbox
                                hasCheckboxAll
                                searchable={isSearchable}
                                placeholder={t('selectAnOption')}
                                portalClassName="multi-filter-multi-select"
                                options={selectedFilterOptions}
                              />
                            )}
                          </Field>
                        ) : (
                          <Field
                            name={`${name}.value`}
                            component={SelectField}
                            searchable={isSearchable}
                            icon={<ChevrondownIcon />}
                            renderAsPortal={!isPhablet}
                            portalClassName="multi-filter-select"
                            options={selectedFilterOptions}
                          />
                        ))}

                      {!isSingleElementArray(fields?.value) ? (
                        <Button
                          variant="icon"
                          className="warning-action-button"
                          icon={TrashIcon}
                          onClick={() => fields.remove(index)}
                        />
                      ) : (
                        <div className="button-placeholder" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="multi-filter-options__footer">
              {fields.length < getArrayLength(filters) && (
                <Button
                  className="multi-filter-options__add-filter"
                  icon={PlusIcon}
                  variant="outline"
                  onClick={() => fields.push(INITIAL_VALUE)}
                >
                  {t('addFilter')}
                </Button>
              )}
              {getResultsCount && (
                <ResultsCount
                  filterValues={formFilters}
                  filterOptions={filters}
                  getResultsCount={getResultsCount}
                />
              )}
            </div>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default forwardRef(MultiFilterOptions);
